import React, { useState } from "react";
import { X, Shield, Brain, Users, Lock } from "lucide-react";

interface DataConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConsent: (consented: boolean) => void;
}

export default function DataConsentModal({ isOpen, onClose, onConsent }: DataConsentModalProps) {
  const [hasConsented, setHasConsented] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = () => {
    onConsent(hasConsented);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1a1a1a] border border-gray-700 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
              <Brain className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Help Improve AcronIQ Veritus</h2>
              <p className="text-sm text-gray-400">Make AI research better for everyone</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Main Message */}
          <div className="text-center space-y-3">
            <div className="text-lg text-gray-200">
              Your research conversations help AcronIQ Veritus understand:
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-gray-800/40">
                <Users className="h-6 w-6 text-blue-400" />
                <div className="text-sm text-gray-300 text-center">
                  Better ways to analyze markets and trends
                </div>
              </div>
              <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-gray-800/40">
                <Brain className="h-6 w-6 text-purple-400" />
                <div className="text-sm text-gray-300 text-center">
                  More relevant strategic insights
                </div>
              </div>
              <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-gray-800/40">
                <Shield className="h-6 w-6 text-green-400" />
                <div className="text-sm text-gray-300 text-center">
                  Clearer, more actionable recommendations
                </div>
              </div>
            </div>
          </div>

          {/* Privacy Assurance */}
          <div className="bg-gray-800/30 rounded-xl p-4 border border-gray-700/50">
            <div className="flex items-start gap-3">
              <Lock className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
              <div className="space-y-2">
                <div className="font-medium text-gray-200">Your Privacy is Protected</div>
                <div className="text-sm text-gray-400 space-y-1">
                  <div>• All personal info automatically removed</div>
                  <div>• Company details and sensitive data stripped</div>
                  <div>• Only research patterns and quality feedback used</div>
                  <div>• Full GDPR compliance and data protection</div>
                </div>
              </div>
            </div>
          </div>

          {/* What We Collect vs Don't Collect */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="font-medium text-green-400">✅ What We Collect</div>
              <div className="text-sm text-gray-400 space-y-1">
                <div>• Research questions (anonymized)</div>
                <div>• AI responses and analysis</div>
                <div>• User feedback (thumbs up/down)</div>
                <div>• Conversation flow patterns</div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="font-medium text-red-400">❌ What We Don't Collect</div>
              <div className="text-sm text-gray-400 space-y-1">
                <div>• Personal names or identifiers</div>
                <div>• Company names and details</div>
                <div>• Email addresses or contact info</div>
                <div>• Sensitive financial data</div>
              </div>
            </div>
          </div>

          {/* Consent Checkbox */}
          <div className="border border-gray-700 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={hasConsented}
                onChange={(e) => setHasConsented(e.target.checked)}
                className="mt-1 w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
              />
              <div className="space-y-1">
                <div className="text-gray-200 font-medium">
                  Yes, help improve AcronIQ Veritus with my anonymized interactions
                </div>
                <div className="text-sm text-gray-400">
                  This is optional and you can change your mind anytime in settings.
                </div>
              </div>
            </label>
          </div>

          {/* Additional Info */}
          <div className="text-center text-sm text-gray-500">
            By participating, you're helping build better AI for business research.
            <br />
            <button className="text-blue-400 hover:text-blue-300 underline">
              Learn more about our data practices →
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-700">
          <button
            onClick={() => {
              setHasConsented(false);
              handleSubmit();
            }}
            className="px-4 py-2 text-gray-400 hover:text-gray-200 transition-colors"
          >
            No thanks, keep private
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-gray-200 transition-colors"
            >
              Decide later
            </button>
            <button
              onClick={handleSubmit}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                hasConsented
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
              }`}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
